import React, {useState, useImperativeHandle, forwardRef} from 'react';

import FormField from './FormField';
import {isValidEmail} from '../../../../utils/helper/validation';

export type EmailReceiptInfo = {
  email: string;
};

// Export the ref methods so parent components can access validation
export interface EmailReceiptFormRef {
  validateAll: () => boolean;
  hasErrors: () => boolean;
  getErrors: () => Partial<Record<keyof EmailReceiptInfo, string>>;
}

const EmailReceiptForm = forwardRef<
  EmailReceiptFormRef,
  {
    emailReceiptInfo: EmailReceiptInfo;
    setEmailReceiptInfo: React.Dispatch<React.SetStateAction<EmailReceiptInfo>>;
  }
>((props, ref) => {
  const {emailReceiptInfo, setEmailReceiptInfo} = props;

  // Internal error state - no longer passed from parent
  const [errors, setErrors] = useState<
    Partial<Record<keyof EmailReceiptInfo, string>>
  >({});

  // Expose validation methods to parent via ref
  useImperativeHandle(ref, () => ({
    validateAll: () => {
      const newErrors: Partial<Record<keyof EmailReceiptInfo, string>> = {};

      // Validate all fields
      if (!emailReceiptInfo.email) {
        newErrors.email = 'Email is required';
      } else if (!isValidEmail(emailReceiptInfo.email)) {
        newErrors.email = 'Enter a valid email address';
      }

      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    },

    hasErrors: () => {
      return Object.values(errors).some((error) => error !== '');
    },

    getErrors: () => errors,
  }));

  const validateEmail = (value: string) => {
    if (!value) {
      setErrors((prev) => ({...prev, email: ''}));
      return;
    }
    if (!isValidEmail(value)) {
      setErrors((prev) => ({...prev, email: 'Enter a valid email address'}));
    } else {
      setErrors((prev) => ({...prev, email: ''}));
    }
  };

  const handleInputChange = function handleInputChange(
    field: keyof EmailReceiptInfo,
    value: string
  ) {
    let formattedValue = value;

    switch (field) {
      case 'email':
        formattedValue = value.replaceAll(' ', '');
        setEmailReceiptInfo({...emailReceiptInfo, [field]: formattedValue});
        validateEmail(formattedValue);
        return;
      default:
        break;
    }
  };

  return (
    <div className="w-full">
      <FormField
        placeholder="Email"
        value={emailReceiptInfo.email}
        type="email"
        onChange={(value) => handleInputChange('email', value)}
        error={errors.email}
        className={`${
          errors.email
            ? 'w-full bg-surfaceNegative mb-4'
            : 'w-full border rounded-md border-subtleGrey'
        } `}
      />
    </div>
  );
});

EmailReceiptForm.displayName = 'EmailReceiptForm';

export default EmailReceiptForm;
